<template>
  <div ref="root">
    <Paywall v-if="paywall && paywall.showPaywall" v-bind="paywall" />
    <VuepalWebform
      v-else-if="webform"
      v-bind="webform"
      :submit-form="submitForm"
      :submit-label="$texts('submit', 'Senden')"
      form-class="grid"
      @success="onSuccess"
    />
  </div>
</template>

<script lang="ts" setup>
import type {
  PaywallFragment,
  WebformFragment,
  WebformSubmissionFile,
  WebformSubmissionResultFragment,
} from '#graphql-operations'
import type { MutationVariables } from '~/layers/webform/types/webform'

declare global {
  interface Window {
    grecaptcha: {
      ready: (callback: () => void) => void
      execute: (siteKey: string, options: { action: string }) => Promise<string>
    }
  }
}
await initFormKit()
const root = ref<HTMLElement | null>(null)
const language = useCurrentLanguage()
const config = useRuntimeConfig()

useHead({
  script: [
    {
      src: `https://www.google.com/recaptcha/api.js?render=${config.public.recaptchaSiteKey}`,
    },
  ],
})

const props = defineProps<{
  webform: WebformFragment
  sourceEntityId?: string
  sourceEntityType?: string
}>()

const paywall = computed<PaywallFragment | undefined>(() => {
  return props.webform?.paywall
})

function loadRecaptcha(): Promise<void> {
  return new Promise((resolve) => {
    window.grecaptcha.ready(() => {
      resolve()
    })
  })
}

async function submitForm(arg: any) {
  const input = arg as MutationVariables
  if (!props.webform?.id) {
    return
  }

  if (!root.value) {
    return
  }

  const formdata = new FormData()

  const elements = input.elements.map((v) => {
    return {
      element: v.element,
      value: v.value,
    }
  })

  // The form is only ever submitted client side.
  await loadRecaptcha()
  const token = await window.grecaptcha.execute(
    config.public.recaptchaSiteKey,
    {
      action: 'submit',
    },
  )
  // Add a hidden input with a name of 'g_recaptcha_response' to the form.
  elements.push({
    element: 'g_recaptcha_response',
    value: token,
  })

  // File stuff.
  const files: WebformSubmissionFile[] = []
  const map: Record<string, string[]> = {}

  input.files.forEach((v, index) => {
    files.push({
      element: v.element,
      file: null,
    })
    map[index.toString()] = [`variables.files.${index}.file`]
    formdata.append(index.toString(), v.value)
  })

  const variables: any = {
    id: props.webform.id,
    elements,
    files,
    sourceEntityType: props.sourceEntityType,
    sourceEntityId: props.sourceEntityId,
  }

  formdata.append('variables', JSON.stringify(variables))
  formdata.append('map', JSON.stringify(map))
  formdata.append('webform_id', props.webform.id || '')

  return $fetch<WebformSubmissionResultFragment | null>('/api/webform-submit', {
    method: 'POST',
    body: formdata,
    query: {
      language: language.value,
    },
  })
}

const router = useRouter()

function onSuccess({ id, token }: { id?: string; token?: string }) {
  if (id && token) {
    router.push({
      name: 'webform-confirmation',
      params: {
        id,
      },
      query: {
        token,
        webformId: props.webform?.id,
      },
    })
  }
}
</script>

<style lang="postcss">
.grecaptcha-badge {
  visibility: hidden;
}

.formkit-input::file-selector-button {
  @apply appearance-none p-0 border-0 text-[0px];
}

input:-webkit-autofill,
input:-webkit-autofill:hover,
input:-webkit-autofill:focus,
input:-webkit-autofill:active {
  -webkit-box-shadow: 0 0 0 30px white inset;
}

.formkit-section {
  @apply mt-50 md:mt-80;
}

.formkit-outer + .formkit-section {
  @apply mt-30 md:mt-50;
}

.formkit-section-title {
  @apply font-medium mb-20 text-md md:text-lg;
}

.formkit-flexbox {
  @apply flex -m-10;

  > div {
    @apply w-full md:w-1/2 p-10;
  }
}

.formkit-actions {
  @apply mt-10 md:mt-20;
}
</style>
